import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keyboard behaviour for the card replacement and activation dialogs: focus moves into the
// dialog when it opens, Tab stays inside it, Escape closes it (unless canClose is false, as
// while an order is being placed), and focus goes back to what opened it once it closes.
// Returns the ref for the dialog element, which takes tabIndex={-1}.
const useDialogFocus = ({ isOpen, onClose, canClose = true }) => {
  const dialogRef = useRef(null);
  // The latest handlers, so the listener added on open never acts on stale ones.
  const closeRef = useRef(onClose);
  const canCloseRef = useRef(canClose);
  closeRef.current = onClose;
  canCloseRef.current = canClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const opener = document.activeElement;
    if (dialogRef.current) dialogRef.current.focus();

    const onKeyDown = e => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (e.key === 'Escape') {
        if (canCloseRef.current) {
          e.preventDefault();
          closeRef.current();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = dialog.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Back to the button or link that opened the dialog, if it is still on the page.
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [isOpen]);

  return dialogRef;
};

export default useDialogFocus;
